
import { EventType, ClientEvent } from "matrix-js-sdk";
import {
    CALL_ANSWER,
    CALL_HANGUP,
    CALL_INVITE,
    CALL_REJECT
} from "src/constants/matrixEventTypes";
import { getFilteredTimelineForUser } from "../utils/filteredTimeline";
import { getUnreadCountWithoutCallNotes } from "src/scenes/Chat/utils/unreadNotifications";
import { Toast } from "src/components";
import { getMatrixKeyRecoveryState, MATRIX_KEY_RECOVERY_STATE } from "../utils/matrixKeyRecovery";

const CALL_SUMMARY_MESSAGE_FIELD = "com.memotivationapp.call_summary";

const getDecryptionFailurePreview = matrixEvent => {
    const recoveryState = getMatrixKeyRecoveryState(matrixEvent);

    if (recoveryState === MATRIX_KEY_RECOVERY_STATE.RECOVERING) {
        return "(Encrypted message is still syncing)";
    }
    if (recoveryState === MATRIX_KEY_RECOVERY_STATE.WITHHELD) {
        return "(Encrypted message unavailable)";
    }
    if (recoveryState === MATRIX_KEY_RECOVERY_STATE.EXHAUSTED) {
        return "(Encrypted message could not be recovered)";
    }

    return "(Unable to decrypt message)";
};

const formatCallDuration = durationSeconds => {
    const totalSeconds = Math.max(1, Math.round(Number(durationSeconds) || 0));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const parts = [];

    if (minutes) {
        parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
    }
    if (seconds || parts.length === 0) {
        parts.push(`${seconds} ${seconds === 1 ? "second" : "seconds"}`);
    }

    return parts.join(" ");
};

const formatParticipantNames = participantNames => {
    const uniqueNames = Array.from(new Set(participantNames.filter(Boolean)));

    if (uniqueNames.length <= 2) {
        return uniqueNames.join(" and ");
    }

    return `${uniqueNames.slice(0, -1).join(", ")}, and ${uniqueNames[uniqueNames.length - 1]}`;
};
/**
 * @typedef {import('matrix-js-sdk').MatrixClient} MatrixClient
 * @param {any[]} data
 * @param {MatrixClient} matrixClient
 * @param {Dispatch<SetStateAction<{ id: string; name: string }[]>>} setMatrixChats
 */
class MatrixChat {
    constructor(data, matrixClient, setMatrixChats, updateActiveChatListTotalUnread) {
        this.updateFromApiResponseData(data);
        this.matrixClient = matrixClient; // Store the matrixClient instance
        
        //will be populated when fetchMatrixRoom()
        this.matrixRoom = null;
        this.matrixRoomLatestEvent = null;
        this.matrixRoomLoading = true;
        this.matrixRoomUnreadNotificationCount = 0;
        this.matrixRoomRecentMessage = "";
        this.handleSetMatrixChat = () => {
            setMatrixChats([this]);
            updateActiveChatListTotalUnread();
        };

        this.matrixRoomTimelineLoading = false;
        this.matrixRoomTimeline = [];
        this.isTyping = false;
    }

    updateFromApiResponseData(data) {
        this.created_at = data.created_at;
        this.allowUsersToLeave = data.allowUsersToLeave;
        this.isClient = data.isClient;
        this.isGroupChat = data.isGroupChat;
        this.latestMessageAt = data.latestMessageAt || null; // Default to null if not present
        this.leadershipRoleUuid = data.leadershipRoleUuid || null;
        this.matrixRoomId = data.matrixRoomId;
        this.name = data.name;
        this.ownerUuid = data.ownerUuid;
        this.updated_at = data.updated_at;
        this.users = data.users;
        this.uuid = data.uuid;
    }

    getMyRoomLiveDetails = async () => {
        if (!this.matrixClient?.getRoom) {
            this.matrixRoomLoading = false;
            this.matrixRoomTimelineLoading = false;
            return;
        }

        await this.getRoomWithTimeout(this.matrixClient, this.matrixRoomId);
        await this.fetchMatrixRoom(this.matrixClient);
        await this.fetchMatrixRoomTimeline(100);

        this.handleSetMatrixChat();

        return;
    };

    getRoomWithTimeout = (matrixClient, roomId, timeout = 100, retryDelay = 500, maxRetries = 12) => {
        if (!matrixClient?.getRoom) {
            return Promise.resolve(null);
        }

        let retries = 0;
    
        return new Promise((resolve, reject) => {

            const checkRoom = async () => {
                console.log(`[MatrixChat] Room ${roomId} getRoom(), Retries: ${retries}`);
                const room = matrixClient.getRoom(roomId);
                if (room) {
                    resolve(room);
                    return;
                }
    
                if (retries < maxRetries) {
                    retries++;
                    console.log(`[MatrixChat] Room ${roomId} not found, retrying in ${retryDelay}ms (attempt ${retries}/${maxRetries}).`);
                    setTimeout(checkRoom, retryDelay);
                } else {
                    reject(new Error(`[MatrixChat] Room ${roomId} not found after ${maxRetries} retries.`));
                }
            };
            
            console.log(`[MatrixChat] Room ${roomId} is now syncing with MatrixClient`);
            setTimeout(checkRoom, timeout); // Initial check after timeout
        });
    };

    /**
     * @typedef {import('matrix-js-sdk').MatrixClient} MatrixClient
     * @param {MatrixClient} matrixClient
     * @param activeChatLists Array
     */
    async fetchMatrixRoom(matrixClient) {
        this.matrixRoomLoading = true;
        if (!matrixClient?.getRoom) {
            this.matrixRoomLoading = false;
            return;
        }

        try {
            this.matrixRoom = matrixClient.getRoom(this.matrixRoomId);

            if (this.matrixRoom) {
                await this.decryptTimelineEvents(this.matrixRoom.getLiveTimeline().getEvents());

                const latestMatrixEvent = this.getLatestMessageEvent(this.matrixRoom) || this.matrixRoom.getLastLiveEvent();
                this.matrixRoomLatestEvent = latestMatrixEvent;
                if (!latestMatrixEvent) {
                    console.warn("[MatrixChat] No latest event found in this room.", this.matrixRoom);
                }
               
                this.matrixRoomUnreadNotificationCount = getUnreadCountWithoutCallNotes(
                    this.matrixRoom,
                    matrixClient.getUserId?.() || ""
                );
                this.matrixRoomRecentMessage = this.getRecentMessageEventInTheRoom(this.matrixRoom);
                this.matrixRoomLoading = false;
            }
            //this.matrixRoom = await matrixClient.getRoomSummary(this.matrixRoomId);
        } catch (error) {
            console.error("[MatrixChat] Error getting matrix room:", this.matrixRoom, error);
            // Handle error appropriately, e.g., set an error flag on this
        } finally {
            console.info(`[MatrixChat] ${this.matrixRoomId} is done running fetchMatrixRoom`, this);
        }
    }
    
    // Display typing..
    getDisplayMessage(room) {
        if (this.isTyping) {
            return this.isGroupChat ? "Someone is Typing..." : "Typing...";
        }
        return this.getRecentMessageEventInTheRoom(room);
    }

    getChatParticipantDisplayName = (matrixUserId, room, currentUserId) => {
        if (matrixUserId === currentUserId) {
            return "You";
        }

        const chatUser = this.users?.find(user => user.matrixUserId === matrixUserId);
        const firstName = chatUser?.employeeDetails?.firstName || chatUser?.firstName || "";
        const lastName = chatUser?.employeeDetails?.lastName || chatUser?.lastName || "";
        const chatUserName = `${firstName} ${lastName}`.trim();

        if (chatUserName) {
            return chatUserName;
        }

        const roomMemberName = room.getMember(matrixUserId)?.name || "";
        return roomMemberName.replace(/\s*(?:\([^)]*\)|Mozilla\/.*)$/i, "").trim() || "Someone";
    };

    getCallSummaryPreview = (summary, room, currentUserId) => {
        const isTerminalWithoutConnection = ["cancelled", "declined"].includes(summary?.outcome);
        if (isTerminalWithoutConnection) {
            const endedByName = this.getChatParticipantDisplayName(
                summary.ended_by_user_id,
                room,
                currentUserId
            );

            if (summary.outcome === "declined") {
                return endedByName === "You"
                    ? "You declined the call."
                    : `${endedByName || "The recipient"} declined the call.`;
            }

            return endedByName === "You"
                ? "You cancelled the call."
                : "The call was cancelled.";
        }

        const participantNames = (summary?.participant_user_ids || [])
            .map(matrixUserId => this.getChatParticipantDisplayName(matrixUserId, room, currentUserId))
            .filter(Boolean);
        const names = formatParticipantNames(participantNames.length ? participantNames : ["You"]);
        const duration = formatCallDuration(summary?.duration_seconds);

        return participantNames.length > 1
            ? `${names} were on a call for ${duration}.`
            : `You were on a call for ${duration}.`;
    };

    setTypingStatus = (isTyping, typingMember) => {
        const currentUserId = this.matrixClient?.getUserId?.();
        if (!currentUserId) {
            this.isTyping = false;
            this.handleSetMatrixChat();
            return;
        }
        const isCurrentUserTyping = typingMember?.userId === currentUserId;
        const someoneElseTyping = isTyping && !isCurrentUserTyping;

        this.isTyping = someoneElseTyping;
        this.handleSetMatrixChat();

        if (someoneElseTyping) {
            clearTimeout(this.typingTimeout);
            this.typingTimeout = setTimeout(() => {
                this.isTyping = false;
                this.handleSetMatrixChat();
            }, 5000);
        }
    };
    
    getRecentMessageEventInTheRoom = (room) => {
        try {
            const userId = this.matrixClient.getUserId();

            // Use the utility function to filter timeline based on membership periods
            const filteredTimeline = getFilteredTimelineForUser(
                room,
                room.getLiveTimeline().getEvents(),
                userId
            );

            const messageEvents = filteredTimeline.filter(
                e => e.getType() === EventType.RoomMessage || e.getType() === EventType.RoomMessageEncrypted
            );

            if (!messageEvents || messageEvents.length === 0) {
                return "No messages yet";
            }

            // Get the latest message
            let latestRoomMessageEvent = null;
            for (let i = messageEvents.length - 1; i >= 0; i--) {
                const content = messageEvents[i].getContent();
                if (content && content.msgtype) {
                    latestRoomMessageEvent = messageEvents[i];
                    break;
                }
            }
        
            // Check if the filteredTimeline is empty
            if (!latestRoomMessageEvent) {
                return "No messages yet";
            }

            // Check if the type is reported as encrypted (hence decryption failed and the inner type could not be read)
            const content = latestRoomMessageEvent.getContent();
            if (latestRoomMessageEvent.isEncrypted() && !latestRoomMessageEvent.isDecrypted?.() && (
                latestRoomMessageEvent.isDecryptionFailure?.() ||
                content?.msgtype === "m.bad.encrypted"
            )
            ) {
                return getDecryptionFailurePreview(latestRoomMessageEvent);
            }

            const isSender = latestRoomMessageEvent.getSender() === userId;
            const senderName = this.getChatParticipantDisplayName(
                latestRoomMessageEvent.getSender(),
                room,
                userId
            );

            const callSummary = content[CALL_SUMMARY_MESSAGE_FIELD];
            if (callSummary) {
                return this.getCallSummaryPreview(callSummary, room, userId);
            }

            if (content.msgtype !== "m.text") {
                if (this.isGroupChat) {
                    return isSender ? "You sent an attachment" : `${senderName} sent an attachment`;
                } else {
                    return isSender ? "You sent an attachment" : "Sent you an attachment";
                }
            }
            const messageBody = content.body;
            const isGroupchatMessage = this.isGroupChat ? `${senderName}: ${messageBody}` : `${messageBody}`;
            return isSender ? `You: ${messageBody}` : isGroupchatMessage;

        } catch (e) {
            console.warn("[MatrixChat] Error in `getRecentMessageEventInTheRoom`", e);
            return "Unable to retrieve recent message";
        }
    };

    getLatestMessageEvent = (room) => {
        const timeline = room?.getLiveTimeline?.().getEvents?.() || [];
        const userId = this.matrixClient.getUserId();
        const filteredTimeline = getFilteredTimelineForUser(room, timeline, userId);

        for (let index = filteredTimeline.length - 1; index >= 0; index--) {
            const event = filteredTimeline[index];
            if (event.getType() === EventType.RoomMessage || event.getType() === EventType.RoomMessageEncrypted) {
                return event;
            }
        }

        return null;
    };
    
    /**
     * Deletes a Matrix room.
     * @param {function(): Promise<void>} onDeleteChat - Callback function to execute after successful Matrix room deletion.
     */
    deleteMatrixRoom = async (onDeleteChat) => {
        try {
            const matrixRoom = await this.getRoomWithTimeout(this.matrixClient, this.matrixRoomId);
            if (!matrixRoom) {
                throw new Error("Room not found", this.matrixRoomId);
            }

            await onDeleteChat();
            //forcefully running this event, spamming deleting of chat room fixes this.     
            this.matrixClient.emit(ClientEvent.DeleteRoom, this.matrixRoomId);
            console.log(`Room ${this.matrixRoomId} deleted successfully.`);    
        } catch (error) {
            //when 404 status, remove it from the MatrixChatList
            if (error instanceof Error && error.message === "Chat not found") {
                this.matrixClient.emit(ClientEvent.DeleteRoom, this.matrixRoomId);
            }
            else 
            {
                console.warn("[MatrixChat] Room deletion encountered an error: ", error);
                throw error;
            }
        }
    };

    // Update to load all messages from before to now 
    fetchMatrixRoomTimeline = async (limit = 100) => {
        if (!this.matrixClient?.getRoom) {
            this.matrixRoomTimeline = [];
            this.matrixRoomTimelineLoading = false;
            return this.matrixRoomTimeline;
        }

        try {
            const room = await this.getRoomWithTimeout(this.matrixClient, this.matrixRoomId);
            if (!room) throw new Error("Room not found: " + this.matrixRoomId);

            const timelineSet = room.getUnfilteredTimelineSet();

            await this.matrixClient.scrollback(room, limit);

            let events = timelineSet.getLiveTimeline().getEvents().filter(
                e => [
                    EventType.RoomMessage,
                    EventType.RoomMessageEncrypted,
                    CALL_INVITE,
                    CALL_ANSWER,
                    CALL_HANGUP,
                    CALL_REJECT
                ].includes(e.getType())
            );  
            events = await this.decryptTimelineEvents(events);

            this.matrixRoomTimeline = getFilteredTimelineForUser(this.matrixRoom, events, this.matrixClient.getUserId());
            return this.matrixRoomTimeline;

        } catch (error) {
            console.warn("[MatrixChat] Error fetching timeline:", error);
            Toast.error("Failed to load messages for this chat.");
            this.matrixRoomTimeline = [];
            throw error;
        }
    };

    getMatrixEvents = async () => {
        const matrixRoom = await this.getRoomWithTimeout(this.matrixClient, this.matrixRoomId);

        this.matrixRoomTimelineLoading = true;

        try {
            if (!matrixRoom) {
                throw new Error("Room not found: " + this.matrixRoomId);
            }

            const events = matrixRoom.getLiveTimeline().getEvents().filter(event =>
                event.getType() === EventType.RoomMessage || event.getType() === EventType.RoomMessageEncrypted
            );

            this.matrixRoomTimelineLoading = false;
            return events;
        } catch (error) {
            console.warn("[MatrixChat] Room encountered an error when getting the events: ", error);
            this.matrixRoomTimelineLoading = false;
            throw error;
        }
    };

    decryptTimelineEvents = async (events = []) => {
        const decryptedEvents = [];

        for (const event of events) {
            if (event.isEncrypted?.() && !event.isDecrypted?.()) {
                try {
                    await this.matrixClient.decryptEventIfNeeded(event);
                } catch (error) {
                    console.warn(`[MatrixChat] Failed to decrypt event ${event.getId()}:`, error);
                }
            }
            decryptedEvents.push(event);
        }

        return decryptedEvents;
    };
}

export default MatrixChat;
